import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { format } from 'app/shared/utils/string.util';
import {
    azureResourceSelectMethodOptions,
    commonFileTypeStoreGetMetadataActivityReadSettings,
    commonFileTypeStoreWriteSettingControls,
    commonFileTypeStoreDeleteActivityReadSettings,
    commonBinaryStoreWriteSettingControls,
    expiryDatetimeStartMetadata,
    getCommonBinaryStoreReadSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    azureCloudTypeMetadata,
    authenticationReferenceMethodOptions
} from '../consts/metadata-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { binaryMetadata } from '../dataset/binary-metadata';
import { GlobalVariables } from 'app/services/globals';
import { azureDefaultAuthRefTypes, ConnectionAuthKind, CredentialTypeOptions } from 'shell-models-lib';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { avro, delimited, excel, json, orc, parquet, xml } from 'app/registries/dataflow/dataflow-file-format-metadata';
import { mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { AuthConstants } from 'app/services/authentication/auth-constants';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { type ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

const uriFormat = `adl://<accountName>.${AuthConstants.RESOURCE_DOMAIN_MAP.ADLSGen1}`;
const optionalPath = '/webhdfs/v1';
const validEndpointSuffixes =
    'azuredatalakestore\\.net|caboaccountdogfood\\.net|azuredatalakestore\\.eaglex\\.ic\\.gov|azuredatalakestore\\.microsoft\\.scloud';

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function azureBlobFSAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return {
        type: 'radio-switch',
        nested: 1,
        bind: 'authReferenceMethod',
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
                children:
                    credentialTypeFilter === CredentialTypeOptions.ServicePrincipal
                        ? [
                            // service Principal
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'servicePrincipalId',
                                    label: stringResources.ModelName_ServicePrincipalID,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }],
                                    isLabelVertical: true,
                                    description:
                                        '<a target="_blank" href="https://learn.microsoft.com/azure/data-factory/connector-azure-data-lake-store?tabs=data-factory#use-service-principal-authentication">' +
                                        stringResources.LearnMore +
                                        '</a>',
                                    escapeDescription: false
                                }
                            },
                            {
                                type: 'dynamic-akv-switcher',
                                bind: 'servicePrincipalKey',
                                enableAKVParameterization: true,
                                label: stringResources.ModelName_ServicePrincipalKey,
                                child: {
                                    type: 'parameterizable',
                                    attribute: 'servicePrincipalKey',
                                    disableAddParameter: true,
                                    child: {
                                        type: 'password',
                                        label: stringResources.ModelName_ServicePrincipalKey,
                                        required: true,
                                        isLabelVertical: true
                                    }
                                },
                                validators: [{ type: 'required', errorMessage: '' }],
                                description: format(
                                    stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                    format(
                                        stringResources.ModelName_LinkedServiceTemplate,
                                        stringResources.AzureDataLakeStore_DisplayText
                                    ),
                                    stringResources.ModelName_ServicePrincipalKey
                                )
                            },
                            azureCloudTypeMetadata
                        ]
                        : [
                            // system Assigned Managed Identity
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureDataLakeStore_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-data-lake-store?tabs=data-factory#managed-identity'
                            }
                        ]
            },
            {
                caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

export const azureDataLakeStoreMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(injector, 'ExposureControlService');
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureDataLakeStore.linkedServiceType,
        displayName: stringResources.AzureDataLakeStore_DisplayText,
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: ResourceKind.DataLake
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.ServicePrincipal],
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-AzureDataLake'
        },
        isFirstClass: true,
        supportIncrementalBinaryCopy: true,
        copySinkTypes: [adfModelTypeMap.azureDataLakeStore.copySinkType],
        copySourceTypes: [adfModelTypeMap.azureDataLakeStore.copySourceType],
        supportRootFolder: true,
        supportDatasetV2: true,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        notShowInTypeSelection: !exposureControlService.enableADLSCosmosDataset,
        supportOPDG: GlobalVariables.enableOPDG,
        supportFirstClassParameterExperience: true,
        dataflowSource: [avro, delimited, excel, json, orc, parquet, xml],
        dataflowSink: [avro, delimited, json, orc, parquet],
        locationPattern: {
            folderPath: '([^]*)/[^/]*',
            fileName: '(?:[^]*/)?([^/]*)'
        },
        locationPropertiesRequireParameters: ['folderPath', 'fileName'],
        mergeReadSettingsInTrident: true,
        category: ['Azure'],
        supportGetMetadataActivity: true,
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(false, false, false, false, true),
        storeWriteSettingControls: commonFileTypeStoreWriteSettingControls.concat(expiryDatetimeStartMetadata),
        datasetTypeToStoreReadSettingControls: {
            [binaryMetadata.type]: getCommonBinaryStoreReadSettingControls(false, false, false, true)
        },
        datasetTypeToStoreWriteSettingControls: {
            [binaryMetadata.type]: commonBinaryStoreWriteSettingControls.concat(expiryDatetimeStartMetadata)
        },
        controls: [
            {
                type: 'radio-switch',
                nested: 1,
                label: stringResources.AzureDataLakeStore_AccountName_SelectionMethod,
                description: stringResources.AzureDataLakeStore_AccountName_SelectionMethod_Description,
                options: azureResourceSelectMethodOptions,
                isLabelVertical: true,
                defaultValue: '{dataLakeStoreUri != null || parameterized ? "manual" : "azure"}',
                cases: [
                    {
                        caseValue: 'azure',
                        children: [
                            {
                                type: 'azure-data-lake-store-account-select',
                                bind: {
                                    dataLakeStoreUri: 'dataLakeStoreUri',
                                    subscriptionId: 'subscriptionId',
                                    resourceGroupName: 'resourceGroupName'
                                },
                                showPEStatus: true,
                                validators: {
                                    dataLakeStoreUri: [{ type: 'required', errorMessage: '' }]
                                }
                            }
                        ]
                    },
                    {
                        caseValue: 'manual',
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'dataLakeStoreUri',
                                    label: stringResources.ModelName_AccountURI,
                                    required: true,
                                    placeholder: uriFormat,
                                    validators: [
                                        { type: 'required', errorMessage: '' },
                                        {
                                            type: 'pattern',
                                            pattern: `^(adl|https?)://[^\/\.]+\\.(${validEndpointSuffixes})(${optionalPath})?$|^[*]{10}$`,
                                            errorMessage: format(
                                                stringResources.Validator_ErrorMessage_Pattern_AorB,
                                                uriFormat,
                                                `${uriFormat}${optionalPath}`
                                            )
                                        },
                                        {
                                            type: 'pattern',
                                            pattern: `^(adl|https?)://[a-z0-9\-]{3,}\\.(${validEndpointSuffixes})(${optionalPath})?$|^[*]{10}$`,
                                            errorMessage: stringResources.Validation_StorageAccountNameGen1
                                        }
                                    ],
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'subscriptionId',
                                    label: stringResources.SubscriptionId,
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'resourceGroupName',
                                    label: stringResources.ModelName_ResourceGroupName,
                                    isLabelVertical: true
                                }
                            }
                        ]
                    }
                ]
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'tenant-textbox',
                    bind: 'tenant',
                    validators: [{ type: 'required', errorMessage: '' }],
                    isLabelVertical: true,
                    label: stringResources.ModelName_Tenant,
                    required: true
                }
            },
            {
                type: 'select-switch',
                bind: 'authenticationType',
                label: stringResources.ModelName_AuthenticationType,
                options: mergeUserAuthCredentialOption(
                    [
                        {
                            label: stringResources.AuthenticationType_ManagedServiceIdentity,
                            value: AuthenticationType.ManagedServiceIdentity
                        },
                        {
                            label: stringResources.AuthenticationType_ServicePrincipal,
                            value: AuthenticationType.ServicePrincipal
                        },
                        {
                            label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
                            value: AuthenticationType.UserAssignedManagedIdentity
                        }
                    ],
                    AuthenticationType.UserAuth
                ),
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true,
                defaultValue: AuthenticationType.ManagedServiceIdentity,
                cases: [
                    {
                        caseValue: AuthenticationType.ServicePrincipal,
                        children: [azureBlobFSAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
                    },
                    {
                        caseValue: AuthenticationType.ManagedServiceIdentity,
                        children: !GlobalVariables.isSamiSupportEnabled
                            ? [
                                {
                                    type: 'management-identity-info',
                                    resourceType: stringResources.AzureDataLakeStore_DisplayText,
                                    link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-data-lake-store?tabs=data-factory#managed-identity'
                                }
                            ]
                            : [azureBlobFSAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
                    },
                    {
                        // for UAMI there won't be any radio buttons, since inline mode is not supported.
                        caseValue: AuthenticationType.UserAssignedManagedIdentity,
                        children: [
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    },
                    {
                        caseValue: AuthenticationType.UserAuth,
                        children: [
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                hide: !GlobalVariables.enableConnectorUserAuthCred,
                                filterCredentialType: CredentialTypeOptions.UserAuth,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    }
                ]
            }
        ]
    };

    return metadata;
};
